import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { Icosahedron, Box, Cylinder, Torus, Tetrahedron, Octahedron, Sphere } from '@react-three/drei';
import _ from 'lodash';

import { ConfigContext } from './config';

const SHAPE_MAP = {
  Box: Box,
  Cylinder: Cylinder,
  Torus: Torus,
  Sphere: Sphere,
  Icosahedron: Icosahedron,
  Tetrahedron: Tetrahedron,
  Octahedron: Octahedron,
};
export const SHAPE_KEYS = Object.keys(SHAPE_MAP);

export const ShapeContext = createContext({});

export const generateShapes = (config) => {
  return _.range(config.count).map((i) => {
    const position = _.range(3).map(() => _.random(-config.boundary / 2, config.boundary / 2));
    const rotation = _.range(3).map(() => _.random(0, Math.PI * 2));
    const size = _.random(config.size.min, config.size.max);
    const Component = _.sample(config.types.map((shape) => SHAPE_MAP[shape]));

    const args = (() => {
      switch (Component) {
        case Box:
          return [size, size, size];
        case Cylinder:
          return [1, 1, size];
        case Sphere:
          return [size, 20, 20];
        case Torus:
          return [size, size / 2, 20, 80];
        case Icosahedron:
        case Tetrahedron:
        case Octahedron:
          return [size, 0];
        default:
          return [];
      }
    })();

    return {
      Component,
      position,
      rotation,
      args,
    };
  });
};

export const ShapeProvider = (props) => {
  const { useConfig } = useContext(ConfigContext);

  const count = useConfig('shapes.count');
  const boundary = useConfig('shapes.boundary');
  const size = useConfig('shapes.size');
  const types = useConfig('shapes.types');

  const [shapes, setShapes] = useState(generateShapes({ count, boundary, size, types }));

  const randomizeShapes = useCallback(() => {
    setShapes(generateShapes({ count, boundary, size, types }));
  }, [count, boundary, size, types]);

  useEffect(() => {
    randomizeShapes();
  }, [randomizeShapes]);

  const context = { shapes, randomizeShapes };

  return <ShapeContext.Provider value={context}>{props.children}</ShapeContext.Provider>;
};
